/**
 * CustomerMessagesPanel
 * ─────────────────────
 * Unified customer-level Messages surface. Aggregates BOTH:
 *   - Customer Conversations (System A: SMS / calls / notes — `conversations` + `messages`)
 *   - Internal Job Messages   (System B: routed team comms — `jobMessages`)
 *
 * Read-layer unification only — the two underlying data models stay separate.
 * This panel is the single place a user sees every message tied to a customer.
 *
 * Acceptance criteria (Sidebar Fold Plan, Phase 1):
 *   1. Full thread view — collapsible per thread, scrollable
 *   2. Reply/send — composer routes by thread type:
 *        • customer threads → outbound SMS message into Conversations
 *        • job threads      → JobMessage routed to job participants
 *   3. Unread indicators — per-thread badges + aggregate count
 *   4. Per-thread badge — customer threads show channel; job threads show job ID
 *   5. "View Job" action — visible only when thread is job-scoped
 */

import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAppData } from "@/data/AppDataContext";
import { createJobMessage, deriveJobParticipants } from "@/data/jobMessageTypes";
import type { Job } from "@/data/types";
import type { Conversation, Message } from "@/data/communicationTypes";
import {
  MessageSquare,
  Send,
  ExternalLink,
  Clock,
  Inbox,
  ChevronDown,
  ChevronRight,
  Phone,
  MessageCircle,
  StickyNote,
} from "lucide-react";

interface CustomerMessagesPanelProps {
  customerJobs: Job[];
  customerName: string;
}

const CURRENT_USER_ID = "current-user";
const CURRENT_USER_NAME = "You";

function formatTime(ts: string) {
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

/**
 * Tokenize a customer name into match-able last-name tokens.
 * Strips ampersands, "and", common joiners; returns lowercased tokens of len ≥ 3.
 * "James & Diane Chen" → ["james", "diane", "chen"]
 */
function nameTokens(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/[&,.]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length >= 3 && t !== "and");
}

// ── Unified thread shape ──
type CustomerThread = {
  kind: "customer";
  id: string; // conversation id
  conversation: Conversation;
  messages: Message[];
  unreadCount: number;
  lastTs: number;
};

type JobThread = {
  kind: "job";
  id: string; // job id
  job: Job;
  messages: ReturnType<typeof useAppData>["jobMessages"];
  unreadCount: number;
  lastTs: number;
};

type Thread = CustomerThread | JobThread;

export function CustomerMessagesPanel({ customerJobs, customerName }: CustomerMessagesPanelProps) {
  const {
    jobMessages,
    setJobMessages,
    conversations,
    messages: convMessages,
    setMessages: setConvMessages,
    setConversations,
  } = useAppData();

  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Match conversations to this customer:
  //  primary:   linkedJobId is one of customer's jobs (strongest signal)
  //  fallback:  contactName shares a name token with customer name (≥3 chars)
  const customerConversations = useMemo(() => {
    const jobIdSet = new Set(customerJobs.map((j) => j.id));
    const tokens = new Set(nameTokens(customerName));
    return conversations.filter((c) => {
      if (c.linkedJobId && jobIdSet.has(c.linkedJobId)) return true;
      const ct = nameTokens(c.contactName);
      return ct.some((t) => tokens.has(t));
    });
  }, [conversations, customerJobs, customerName]);

  // Build unified thread list
  const threads = useMemo<Thread[]>(() => {
    const out: Thread[] = [];

    // Customer (Conversations) threads
    customerConversations.forEach((conv) => {
      const msgs = convMessages
        .filter((m) => m.conversationId === conv.id)
        .slice()
        .sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        );
      const lastTs = msgs.length
        ? new Date(msgs[msgs.length - 1].timestamp).getTime()
        : new Date(conv.updatedAt).getTime();
      out.push({
        kind: "customer",
        id: `conv:${conv.id}`,
        conversation: conv,
        messages: msgs,
        unreadCount: conv.unreadCount ?? 0,
        lastTs,
      });
    });

    // Job (JobMessages) threads
    const jobIdSet = new Set(customerJobs.map((j) => j.id));
    const jobMsgsByJob = new Map<string, typeof jobMessages>();
    customerJobs.forEach((j) => jobMsgsByJob.set(j.id, []));
    jobMessages.forEach((m) => {
      if (!jobIdSet.has(m.jobId)) return;
      jobMsgsByJob.get(m.jobId)!.push(m);
    });
    customerJobs.forEach((job) => {
      const msgs = (jobMsgsByJob.get(job.id) ?? [])
        .slice()
        .sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        );
      if (msgs.length === 0) return; // hide empty job threads
      const unreadCount = msgs.filter(
        (m) => m.recipientStatuses?.[CURRENT_USER_ID] === "unread",
      ).length;
      const lastTs = new Date(msgs[msgs.length - 1].timestamp).getTime();
      out.push({
        kind: "job",
        id: `job:${job.id}`,
        job,
        messages: msgs,
        unreadCount,
        lastTs,
      });
    });

    return out.sort((a, b) => b.lastTs - a.lastTs);
  }, [customerConversations, convMessages, customerJobs, jobMessages]);

  const totalUnread = useMemo(
    () => threads.reduce((sum, t) => sum + t.unreadCount, 0),
    [threads],
  );

  // Auto-expand most recent thread on first render
  useEffect(() => {
    if (!expandedThreadId && threads.length > 0) {
      setExpandedThreadId(threads[0].id);
    }
  }, [threads, expandedThreadId]);

  // Mark unread → read when a thread expands (optimistic, local only)
  useEffect(() => {
    if (!expandedThreadId) return;
    const t = threads.find((x) => x.id === expandedThreadId);
    if (!t || t.unreadCount === 0) return;

    if (t.kind === "job") {
      setJobMessages((prev) =>
        prev.map((m) => {
          if (m.jobId !== t.job.id) return m;
          if (m.recipientStatuses?.[CURRENT_USER_ID] !== "unread") return m;
          return {
            ...m,
            recipientStatuses: {
              ...m.recipientStatuses,
              [CURRENT_USER_ID]: "read",
            },
          };
        }),
      );
    } else {
      // Customer (conversation) thread — clear unreadCount on the conversation
      setConversations((prev) =>
        prev.map((c) =>
          c.id === t.conversation.id ? { ...c, unreadCount: 0 } : c,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedThreadId]);

  const handleSend = (t: Thread) => {
    const draft = drafts[t.id]?.trim();
    if (!draft) return;

    if (t.kind === "job") {
      const participants = deriveJobParticipants(t.job);
      const recipientIds = Object.values(participants).filter(Boolean) as string[];
      const msg = createJobMessage(
        t.job.id,
        draft,
        { id: CURRENT_USER_ID, name: CURRENT_USER_NAME, role: "ops" },
        recipientIds,
        { context: "Customer Profile", priority: "normal" },
      );
      setJobMessages((prev) => [...prev, msg]);
    } else {
      // Customer (Conversations) — outbound SMS
      const now = new Date().toISOString();
      const newMsg: Message = {
        id: `MSG-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        conversationId: t.conversation.id,
        direction: "outbound",
        channel: "sms",
        body: draft,
        sender: CURRENT_USER_NAME,
        timestamp: now,
        deliveryStatus: "sent",
      };
      setConvMessages((prev) => [...prev, newMsg]);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === t.conversation.id
            ? { ...c, lastMessagePreview: draft, updatedAt: now }
            : c,
        ),
      );
    }

    setDrafts((prev) => ({ ...prev, [t.id]: "" }));
  };

  return (
    <div className="flex flex-col h-full border border-border/60 rounded-md bg-background overflow-hidden shadow-none">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-border/60 flex items-center justify-between">
        <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.14em] flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5" /> Messages
          <span className="tabular-nums text-muted-foreground/70">({threads.length})</span>
        </h3>
        {totalUnread > 0 && (
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border border-primary/30 bg-primary/10 text-primary tabular-nums"
            aria-label={`${totalUnread} unread`}
          >
            {totalUnread} unread
          </span>
        )}
      </div>

      {/* Threads */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Inbox className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-xs font-medium">No messages yet</p>
            <p className="text-[10px] mt-1">
              {customerJobs.length === 0
                ? "This customer has no jobs"
                : "No customer or internal messages on file"}
            </p>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 divide-y divide-border/60 overflow-y-auto">
            {threads.map((t) => {
              const isExpanded = expandedThreadId === t.id;
              const anyExpanded = expandedThreadId !== null;
              return (
                <div
                  key={t.id}
                  className={`bg-background flex flex-col min-h-0 ${isExpanded ? "flex-1" : "shrink-0"} ${anyExpanded && !isExpanded ? "hidden" : ""}`}
                >
                  {/* Thread header */}
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedThreadId(isExpanded ? null : t.id)
                    }
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-muted/40 transition-colors text-left"
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}

                    {t.kind === "customer" ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] shrink-0 border-info/40 text-info"
                      >
                        Customer
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-[10px] font-mono shrink-0"
                      >
                        {t.job.id}
                      </Badge>
                    )}

                    <span className="text-[11px] font-medium text-foreground truncate flex-1">
                      {t.kind === "customer"
                        ? t.conversation.contactName
                        : t.job.projectType ?? "Job"}
                    </span>

                    {t.unreadCount > 0 && (
                      <Badge className="text-[10px] bg-primary text-primary-foreground shrink-0">
                        {t.unreadCount}
                      </Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {t.messages.length}{" "}
                      {t.messages.length === 1 ? "msg" : "msgs"}
                    </span>
                  </button>

                  {/* Expanded thread */}
                  {isExpanded && (
                    <div className="px-3 pb-3 flex flex-col flex-1 min-h-0 bg-muted/10">
                      {/* Header strip */}
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {t.kind === "customer"
                            ? `SMS / call thread · ${t.messages.length} message${t.messages.length === 1 ? "" : "s"}`
                            : `Internal team thread · ${t.messages.length} message${t.messages.length === 1 ? "" : "s"}`}
                        </span>
                        {t.kind === "job" && (
                          <Button
                            asChild
                            variant="ghost"
                            size="sm"
                            className="h-6 text-[10px] gap-1"
                          >
                            <Link to={`/jobs/${t.job.id}`}>
                              <ExternalLink className="h-3 w-3" />
                              View Job
                            </Link>
                          </Button>
                        )}
                        {t.kind === "customer" && t.conversation.linkedJobId && (
                          <Button
                            asChild
                            variant="ghost"
                            size="sm"
                            className="h-6 text-[10px] gap-1"
                          >
                            <Link to={`/jobs/${t.conversation.linkedJobId}`}>
                              <ExternalLink className="h-3 w-3" />
                              View Job
                            </Link>
                          </Button>
                        )}
                      </div>

                      {/* Messages — fills available space */}
                      <div className="flex-1 min-h-0 space-y-1.5 overflow-y-auto pr-1 py-2">
                        {t.kind === "customer"
                          ? t.messages.map((m) => {
                              const isOutbound = m.direction === "outbound";
                              const ChannelIcon =
                                m.channel === "call"
                                  ? Phone
                                  : m.channel === "note"
                                    ? StickyNote
                                    : MessageCircle;
                              return (
                                <div
                                  key={m.id}
                                  className={`flex flex-col ${isOutbound ? "items-end" : "items-start"}`}
                                >
                                  <div
                                    className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[11px] ${
                                      isOutbound
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-muted text-foreground"
                                    }`}
                                  >
                                    <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider opacity-70 mb-0.5">
                                      <ChannelIcon className="h-2.5 w-2.5" />
                                      {m.sender}
                                      {m.channel === "call" ? " · call" : ""}
                                    </div>
                                    <p className="leading-relaxed whitespace-pre-wrap">
                                      {m.body}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 mt-0.5 px-1">
                                    <Clock className="h-2.5 w-2.5 text-muted-foreground/60" />
                                    <span className="text-[9px] text-muted-foreground">
                                      {formatTime(m.timestamp)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })
                          : t.messages.map((m) => {
                              const isMine = m.senderId === CURRENT_USER_ID;
                              return (
                                <div
                                  key={m.id}
                                  className={`flex flex-col ${isMine ? "items-end" : "items-start"}`}
                                >
                                  <div
                                    className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[11px] ${
                                      isMine
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-muted text-foreground"
                                    }`}
                                  >
                                    {!isMine && (
                                      <div className="text-[9px] font-bold uppercase tracking-wider opacity-70 mb-0.5">
                                        {m.senderName}
                                        {m.senderRole ? ` · ${m.senderRole}` : ""}
                                      </div>
                                    )}
                                    <p className="leading-relaxed whitespace-pre-wrap">
                                      {m.body}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 mt-0.5 px-1">
                                    <Clock className="h-2.5 w-2.5 text-muted-foreground/60" />
                                    <span className="text-[9px] text-muted-foreground">
                                      {formatTime(m.timestamp)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                      </div>

                      {/* Composer — pinned to bottom */}
                      <div className="flex items-center gap-2 pt-2 mt-1 border-t border-border/60 shrink-0">
                        <Input
                          value={drafts[t.id] ?? ""}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [t.id]: e.target.value,
                            }))
                          }
                          placeholder={
                            t.kind === "customer"
                              ? `Reply to ${t.conversation.contactName}...`
                              : `Internal note on ${t.job.id}...`
                          }
                          className="h-8 text-xs flex-1"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSend(t);
                            }
                          }}
                        />
                        <Button
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => handleSend(t)}
                          disabled={!(drafts[t.id]?.trim())}
                          aria-label="Send"
                        >
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
